import React, { useEffect } from 'react';
import { cn } from '../../lib/utils';
import useWorkflowCanvasViewport from '../../hooks/useWorkflowCanvasViewport';
import { buildCanvasGridStyle, WORKFLOW_CANVAS_GRID } from '../../utils/workflowCanvasViewportUtils';
import WorkflowCanvasControls from './WorkflowCanvasControls';
import WorkflowPathInfoCard from './WorkflowPathInfoCard';
import WorkflowStatsView from './WorkflowStatsView';

const WorkflowCanvasViewport = ({
  children,
  className,
  executionPathView = null,
  onExitExecutionPath,
  statsViewActive = false,
  workflowStatus,
  autoFitOnMount = true,
}) => {
  const viewport = useWorkflowCanvasViewport();

  useEffect(() => {
    if (!autoFitOnMount) return undefined;
    const timer = window.setTimeout(() => {
      viewport.fitToScreen();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoFitOnMount, viewport.fitToScreen]);

  useEffect(() => {
    if (!executionPathView) return undefined;
    const timer = window.setTimeout(() => {
      viewport.fitToScreen();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [executionPathView, viewport.fitToScreen]);

  return (
    <div className={cn('relative min-h-0 flex-1', className)} data-testid="workflow-canvas-viewport-root">
      <div
        ref={viewport.viewportRef}
        className="relative h-full min-h-[480px] w-full overflow-hidden"
        style={{ ...viewport.viewportStyle, backgroundColor: WORKFLOW_CANVAS_GRID.backgroundColor }}
        onPointerDown={viewport.handleViewportPointerDown}
        data-testid="workflow-canvas-viewport"
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={buildCanvasGridStyle(viewport.pan)}
          data-testid="workflow-canvas-grid"
        />

        <div
          ref={viewport.contentRef}
          className="absolute left-0 top-0 w-max will-change-transform"
          style={viewport.contentStyle}
          data-workflow-canvas-content
          data-workflow-pan-layer
          data-testid="workflow-canvas-content"
        >
          {typeof children === 'function' ? children(viewport) : children}
        </div>

        {executionPathView ? (
          <WorkflowPathInfoCard
            executionPathView={executionPathView}
            onExit={onExitExecutionPath}
          />
        ) : statsViewActive ? (
          <WorkflowStatsView hasStats={workflowStatus === 'active'} />
        ) : null}

        <WorkflowCanvasControls
          zoom={viewport.zoom}
          panMode={viewport.panMode}
          onTogglePanMode={viewport.togglePanMode}
          onZoomIn={viewport.zoomIn}
          onZoomOut={viewport.zoomOut}
          onFitToScreen={viewport.fitToScreen}
        />
      </div>
    </div>
  );
};

export default WorkflowCanvasViewport;
